import React, { memo, useMemo, useState, useEffect, useCallback } from 'react'
import classnames from 'classnames'
import { Drawer } from '@pushly/aqe/lib/components'
import Well from '@pushly/aqe/lib/components/well/well'
import { Popover, Alert } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useForm } from 'antd/lib/form/Form'
import { extract } from '../../../../_utils/object'
import { simpleNotification } from '../../../../_utils/utils'
import { INotificationFieldSuggestions } from '../../../../interfaces/notification-field-suggestions'
import { FieldSuggestionResultsManagerProps, SuggestedFieldsForm, VariantState, VariantAction } from './types'
import { extractFormSelections, transformVariantStatesForSubmission, canShowField } from './helpers'
import PageAnalyzerResults from './components/page-analyzer-results'
import PushlyAiResults from './components/pushly-ai-results'
import { ErrorBoundary } from './components/error-boundary'
import '../../styles/notification-field-suggestions.scss'

/**
 * FieldSuggestionResultsManager - Main container component
 *
 * Manages the overall state and props interface, determines which child component
 * to render based on result source, handles drawer visibility and submission logic,
 * and maintains backward compatibility with existing prop interface.
 */
export const FieldSuggestionResultsManager = memo((props: FieldSuggestionResultsManagerProps) => {
    const { domain, results, onClose, onAccept } = props
    const [form] = useForm<SuggestedFieldsForm>()
    const [selectedKeywords, setSelectedKeywords] = useState<string[] | null>(null)

    // New unified state management for AI variants
    const [variantStates, setVariantStates] = useState<Record<number, VariantState>>({})

    // Get current variant data (for non-AI sources)
    const currentVariantData = useMemo(() => {
        if (!results?.data || results.data.length === 0) return null
        return results.data[0] // For page analyzer, just use the first (and only) result
    }, [results?.data])

    // Initialize selected keywords for page analyzer
    const suggestedKws = useMemo<string[]>(() => {
        const keywords = (currentVariantData ? extract(currentVariantData, 'keywords', []) : []) as string[]
        // Normalize keywords to lowercase
        return keywords.map((kw) => kw.toLowerCase())
    }, [currentVariantData?.keywords])

    const isAiSource = results?.source === 'pushly_ai'

    // Initialize variant states when drawer opens or results change
    useEffect(() => {
        if (isAiSource && results?.data) {
            const initialStates: Record<number, VariantState> = {}
            results.data.forEach((variant, index) => {
                // Pre-populate with default selections (all fields checked)
                const selections: Partial<INotificationFieldSuggestions> = {}

                if (variant.title && canShowField(domain, 'title')) {
                    selections.title = variant.title
                }
                if (variant.body && canShowField(domain, 'body')) {
                    selections.body = variant.body
                }
                if (variant.imageUrl && canShowField(domain, 'imageUrl')) {
                    if (variant.compositeImage) {
                        selections.compositeImage = variant.compositeImage
                        selections.imageUrl = variant.imageUrl
                    } else {
                        const imageUrl = variant.compositeImage
                            ? extract(variant.compositeImage, 'url')
                            : variant.imageUrl
                        selections.imageUrl = imageUrl
                    }
                }

                initialStates[index] = {
                    enabled: false,
                    selectedAction: null,
                    selections,
                }
            })
            setVariantStates(initialStates)
        }
    }, [isAiSource, results?.data, domain])

    // Reset variant states when drawer closes
    useEffect(() => {
        if (results === null) {
            setVariantStates({})
            setSelectedKeywords(null)
            form.resetFields()
        }
    }, [results])

    useEffect(() => {
        const hasKwSuggestions = suggestedKws.length > 0

        if (!isAiSource && hasKwSuggestions && selectedKeywords === null) {
            setSelectedKeywords(suggestedKws)
        }

        // Initialize keywords for AI results
        if (isAiSource && results?.data && results.data.length > 0 && selectedKeywords === null) {
            const firstVariant = results.data[0]
            const keywords = (firstVariant ? extract(firstVariant, 'keywords', []) : []) as string[]
            const normalized = keywords.map((kw) => kw.toLowerCase())
            if (normalized.length > 0) {
                setSelectedKeywords(normalized.sort().slice(0, 20)) // MAX_KEYWORDS = 20
            }
        }
    }, [isAiSource, suggestedKws, selectedKeywords, results?.data])

    // Event handlers for new unified state model
    const handleVariantEnabledChange = useCallback((variantIndex: number, enabled: boolean) => {
        setVariantStates((prev) => {
            const newState = {
                enabled,
                selectedAction: enabled ? prev[variantIndex]?.selectedAction ?? null : null,
                selections: prev[variantIndex]?.selections ?? {},
            }
            return {
                ...prev,
                [variantIndex]: newState,
            }
        })
    }, [])

    const handleVariantActionChange = useCallback((variantIndex: number, action: VariantAction | null) => {
        setVariantStates((prev) => {
            const newState = {
                enabled: prev[variantIndex]?.enabled ?? false,
                selectedAction: action,
                selections: prev[variantIndex]?.selections ?? {},
            }
            return {
                ...prev,
                [variantIndex]: newState,
            }
        })
    }, [])

    // Handle field selection changes from VariantCard
    const handleFieldSelectionsChange = useCallback(
        (variantIndex: number, selections: Partial<INotificationFieldSuggestions>) => {
            setVariantStates((prev) => ({
                ...prev,
                [variantIndex]: {
                    ...prev[variantIndex],
                    selections,
                },
            }))
        },
        [],
    )

    // Determine if suggestions are empty
    const suggestionsAreEmpty =
        results === null ||
        !results.data ||
        results.data.length === 0 ||
        results.data.every((variant) => {
            return Object.values(variant).every((v) => v === null || v === undefined)
        })

    // Generate submit text based on source and selections
    const submitText = useMemo(() => {
        if (suggestionsAreEmpty) {
            return 'Go Back'
        }

        if (isAiSource) {
            const enabledVariants = Object.values(variantStates).filter((v) => v.enabled)
            const totalSelections = enabledVariants.length
            return totalSelections > 1 ? 'Accept Selections' : 'Accept Selection'
        }

        return 'Accept Selections'
    }, [suggestionsAreEmpty, isAiSource, variantStates])

    // Validation for AI source submissions
    const builderVariantCount = props.builder?.variants?.length ?? 1
    const validationError = useMemo(() => {
        if (!isAiSource) return null

        const enabledVariants = Object.values(variantStates).filter((v) => v.enabled)

        // Disable submit when no variants are enabled
        if (enabledVariants.length === 0) return 'Enable at least one variant to continue'

        // Check all enabled variants have actions
        const missingActions = enabledVariants.some((v) => v.selectedAction === null)
        if (missingActions) {
            return 'Please select an action for all enabled variants'
        }

        // Check max variants not exceeded — count slots beyond current builder count as new additions
        const maxVariants = 4
        const addCount = enabledVariants.filter(
            (v) => v.selectedAction && v.selectedAction.targetIndex >= builderVariantCount,
        ).length
        if (builderVariantCount + addCount > maxVariants) {
            return `Cannot exceed ${maxVariants} variants`
        }

        return null
    }, [isAiSource, variantStates, builderVariantCount])

    // Handle drawer submission - memoized for performance
    const handleSubmit = useCallback(async () => {
        try {
            if (suggestionsAreEmpty) {
                onClose?.()
                return
            }

            if (isAiSource) {
                // Validate before submission
                if (validationError) {
                    simpleNotification('error', validationError)
                    return
                }

                // Transform the new state model to legacy format for submission
                // Slots with targetIndex < builderVariantCount overwrite existing variants;
                // slots >= builderVariantCount create new variants.
                const { replaceVariants, addVariants } = transformVariantStatesForSubmission(
                    variantStates,
                    results?.data || [],
                    selectedKeywords,
                    domain,
                    builderVariantCount,
                )

                const firstReplace = replaceVariants.length > 0 ? replaceVariants[0] : null
                const additionalReplacements = replaceVariants.length > 1 ? replaceVariants.slice(1) : undefined

                // Collect all enabled AI variant indices for auto-fill synchronization
                const usedVariantIndices = Object.entries(variantStates)
                    .filter(([, state]) => state.enabled && state.selectedAction !== null)
                    .map(([index]) => parseInt(index, 10))

                await onAccept?.(
                    firstReplace?.selections ?? null,
                    addVariants,
                    firstReplace?.targetIndex,
                    additionalReplacements,
                    usedVariantIndices,
                )
            } else {
                // For page analyzer, use form data to build selections
                const values = form.getFieldsValue()
                const selections = extractFormSelections(values, currentVariantData!, domain, selectedKeywords)
                await onAccept?.(selections)
            }
        } catch (error) {
            console.error('Error submitting field suggestions:', error)
            simpleNotification('error', 'An error occurred while submitting your selections')
        }
    }, [
        suggestionsAreEmpty,
        onClose,
        isAiSource,
        variantStates,
        results?.data,
        selectedKeywords,
        domain,
        builderVariantCount,
        onAccept,
        form,
        currentVariantData,
        validationError,
    ])

    // Generate footer content for AI source with selection summary
    const drawerFooter = useMemo(() => {
        if (!isAiSource || !results) return undefined

        const enabledVariants = Object.entries(variantStates).filter(
            ([, state]) => state.enabled && state.selectedAction,
        )

        // If no selections, show default state
        if (enabledVariants.length === 0) {
            return (
                <>
                    <div className="drawer-footer-summary">
                        <span className="summary-text no-selections">
                            <strong>Selections:</strong> None
                        </span>
                    </div>
                    <Well.Buttons
                        onSubmit={handleSubmit}
                        submitText={submitText}
                        disableSubmit={!!validationError}
                        disabledSubmitTooltip={validationError || undefined}
                        hideCancel={true}
                    />
                </>
            )
        }

        // Count new vs overwrite actions based on slot targeting
        const addCount = enabledVariants.filter(
            ([, state]) => state.selectedAction && state.selectedAction.targetIndex >= builderVariantCount,
        ).length
        const replaceCount = enabledVariants.filter(
            ([, state]) => state.selectedAction && state.selectedAction.targetIndex < builderVariantCount,
        ).length

        // Build summary text
        const parts: string[] = []
        if (addCount > 0) {
            parts.push(`${addCount} new test variant${addCount > 1 ? 's' : ''}`)
        }
        if (replaceCount > 0) {
            parts.push(`${replaceCount} replacement${replaceCount > 1 ? 's' : ''}`)
        }
        const summaryText = parts.join(', ')

        // Build popover content grouped by action type, mirroring the dropdown OptGroup structure
        const newSlotVariants = enabledVariants.filter(
            ([, state]) => state.selectedAction && state.selectedAction.targetIndex >= builderVariantCount,
        )
        const existingSlotVariants = enabledVariants.filter(
            ([, state]) => state.selectedAction && state.selectedAction.targetIndex < builderVariantCount,
        )

        const popoverContent = (
            <div className="selection-popover-content">
                {newSlotVariants.length > 0 && (
                    <div className="popover-group">
                        <div className="popover-group-label">Use as New</div>
                        {newSlotVariants.map(([index]) => {
                            const variantNum = parseInt(index, 10) + 1
                            return (
                                <div key={index} className="popover-item">
                                    Suggestion {variantNum} as new test variant
                                </div>
                            )
                        })}
                    </div>
                )}
                {existingSlotVariants.length > 0 && (
                    <div className="popover-group">
                        <div className="popover-group-label">Use as Existing</div>
                        {existingSlotVariants.map(([index, state]) => {
                            const variantNum = parseInt(index, 10) + 1
                            const targetIndex = state.selectedAction!.targetIndex
                            const letter = String.fromCharCode(65 + targetIndex)
                            const slotLabel = builderVariantCount === 1 ? 'Notification' : `Variant ${letter}`
                            return (
                                <div key={index} className="popover-item">
                                    Suggestion {variantNum} as {slotLabel}
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        )

        return (
            <>
                <div className="drawer-footer-summary">
                    <span className="summary-text">
                        <strong>Selections:</strong> {summaryText}
                        <Popover
                            title={<b>Selections</b>}
                            content={popoverContent}
                            placement="top"
                            trigger="hover"
                            overlayClassName="selections-summary-popover"
                        >
                            <InfoCircleOutlined className="info-icon" />
                        </Popover>
                    </span>
                </div>
                <Well.Buttons
                    onSubmit={handleSubmit}
                    submitText={submitText}
                    disableSubmit={!!validationError}
                    disabledSubmitTooltip={validationError || undefined}
                    hideCancel={true}
                />
            </>
        )
    }, [isAiSource, results, variantStates, builderVariantCount, handleSubmit, submitText, validationError])

    return (
        <Drawer
            className={classnames('notification-field-suggestions-drawer', `source-${results?.source ?? 'unknown'}`)}
            title={isAiSource ? 'Pushly AI Generated Results' : 'Page Analysis Results'}
            open={results !== null}
            mask={{
                closable: true,
            }}
            destroyOnHidden={true}
            onClose={onClose}
            onSubmit={handleSubmit}
            submitText={submitText}
            footer={drawerFooter}
            width={800}
        >
            {results !== null && (
                <ErrorBoundary>
                    <div className="pre-form-details">
                        {suggestionsAreEmpty && results.error ? (
                            <Alert
                                type="warning"
                                showIcon={true}
                                message={
                                    <span>
                                        {results.error.message}
                                        {results.error.referenceUrl && (
                                            <>
                                                {' '}
                                                See our{' '}
                                                <a
                                                    href={results.error.referenceUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    documentation
                                                </a>{' '}
                                                for more information.
                                            </>
                                        )}
                                    </span>
                                }
                            />
                        ) : isAiSource ? (
                            <PushlyAiResults
                                domain={domain}
                                results={results.data}
                                builder={props.builder}
                                variantStates={variantStates}
                                selectedKeywords={selectedKeywords}
                                setSelectedKeywords={setSelectedKeywords}
                                onVariantEnabledChange={handleVariantEnabledChange}
                                onVariantActionChange={handleVariantActionChange}
                                onFieldSelectionsChange={handleFieldSelectionsChange}
                            />
                        ) : (
                            <PageAnalyzerResults
                                domain={domain}
                                currentVariantData={currentVariantData!}
                                form={form}
                                selectedKeywords={selectedKeywords}
                                setSelectedKeywords={setSelectedKeywords}
                            />
                        )}
                    </div>
                </ErrorBoundary>
            )}
        </Drawer>
    )
})

FieldSuggestionResultsManager.displayName = 'FieldSuggestionResultsManager'
